import { Injectable } from '@angular/core';
import { RouterState } from '@angular/router';
import {
  BrandAirPlaySecurityConfigurationService,
  LocationAirPlaySecurityConfigurationService,
  RoomAirPlaySecurityConfigurationService,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, concat, of } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';

import { safeMap } from '../effects.helpers';
import { Category } from '../router/category';
import { CategoryType } from '../shared/types';
import {
  CategoryAirplaySecurityProfileApiActions,
  CategoryAirplaySecurityProfileResolverActions,
  CategoryAirplaySecurityProfileViewActions,
} from './category-airplay-security-profile.actions';
import { selectAirplaySecurityProfileLinkedToCategory } from './category-airplay-security-profile.selector';
import {
  CategoryAirplaySecurityProfileConfiguration,
  airplaySecurityProfileFrom,
} from './category-airplay-security-profile.state';

@Injectable()
export class CategoryAirplaySecurityProfileEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly brandAirplaySecurityConfigurationService: BrandAirPlaySecurityConfigurationService,
    private readonly locationAirplaySecurityConfigurationService: LocationAirPlaySecurityConfigurationService,
    private readonly roomAirplaySecurityConfigurationService: RoomAirPlaySecurityConfigurationService,
    private readonly routeStore: Store<RouterState>
  ) {}

  // Our API supports 0-N relations between categories and configurations. Our UI supports 0-1 relations. So we always pick
  // the first relation from the API when loading them.
  loadCategoryAirplaySecurityProfileEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAirplaySecurityProfileResolverActions.categoryProfileRequested),
      mergeMap((action) =>
        safeMap(
          this.loadCategoryAirplaySecurityProfile(action.categoryType, action.id).pipe(
            filter((configurations) => configurations.length > 0),
            map((configurations) => configurations[0])
          ),
          (configuration) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileLoaded({
              configuration,
              categoryType: action.categoryType,
            }),
          (error) => CategoryAirplaySecurityProfileApiActions.categoryProfileLoadedError({ error })
        )
      )
    );
  });

  linkAirplaySecurityProfileToCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAirplaySecurityProfileViewActions.profileSelected),
      mergeMap((action) => {
        const category: Category = {
          categoryType: action.categoryType,
          categoryId: action.categoryId,
          parent: undefined,
        };
        return this.routeStore.select(selectAirplaySecurityProfileLinkedToCategory(category)).pipe(
          filter(
            (existingConfig) =>
              existingConfig ? existingConfig.airPlaySecurityProfilePresetId !== action.preset.uniqueId : true // has change?
          ),
          first(),
          // Use mergeMap to handle the delete/create operations (allows concurrent operations if needed)
          mergeMap((existingConfig) => {
            // Delete the original configuration if it exists, then add the new one
            const delete$: Observable<Action> = existingConfig
              ? this.delete$(category.categoryType, category.categoryId, existingConfig)
              : EMPTY;

            // Add the new configuration
            const create$: Observable<Action> = this.create$(category.categoryType, category.categoryId, {
              uniqueId: action.preset.uniqueId,
            });

            // Use concat to ensure delete happens before create (sequential execution)
            return concat(delete$, create$);
          })
        );
      })
    );
  });

  deleteLinkedAirplaySecurityProfileFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAirplaySecurityProfileViewActions.profileDeselected),
      mergeMap((action) => {
        const category: Category = {
          categoryType: action.categoryType,
          categoryId: action.categoryId,
          parent: undefined,
        };
        return this.routeStore.select(selectAirplaySecurityProfileLinkedToCategory(category)).pipe(
          filter((configuration) => !!configuration),
          first(),
          // Use mergeMap to execute the delete operation (allows concurrent operations if needed)
          mergeMap((configuration) => this.delete$(category.categoryType, category.categoryId, configuration))
        );
      })
    );
  });

  private loadCategoryAirplaySecurityProfile(
    categoryType: CategoryType,
    categoryId: string
  ): Observable<CategoryAirplaySecurityProfileConfiguration[]> {
    switch (categoryType) {
      case 'Brand':
        return this.brandAirplaySecurityConfigurationService
          .getAirPlaySecurityProfilesForBrand(categoryId)
          .pipe(map((configurations) => configurations.map(airplaySecurityProfileFrom)));
      case 'Location':
        return this.locationAirplaySecurityConfigurationService
          .getAirPlaySecurityProfilesForLocation(categoryId)
          .pipe(map((configurations) => configurations.map(airplaySecurityProfileFrom)));
      case 'Room':
        return this.roomAirplaySecurityConfigurationService
          .getAirPlaySecurityProfilesForRoom(categoryId)
          .pipe(map((configurations) => configurations.map(airplaySecurityProfileFrom)));
      default:
        return of([]);
    }
  }

  private readonly create$ = (
    categoryType: CategoryType,
    categoryId: string,
    preset: { uniqueId: string }
  ): Observable<Action> => {
    switch (categoryType) {
      case 'Brand': {
        const brandConfiguration: CategoryAirplaySecurityProfileConfiguration = {
          type: 'Brand',
          id: categoryId,
          airPlaySecurityProfilePresetId: preset.uniqueId,
          displayName: '',
        };

        return safeMap(
          this.brandAirplaySecurityConfigurationService.addAirPlaySecurityProfileToBrand(categoryId, {
            airPlaySecurityProfilePresetId: preset.uniqueId,
          }),
          (configuration) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAdded({
              configuration: airplaySecurityProfileFrom(configuration),
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAddedError({
              configuration: brandConfiguration,
              categoryType,
              error,
            })
        );
      }
      case 'Location': {
        const locationConfiguration: CategoryAirplaySecurityProfileConfiguration = {
          type: 'Location',
          id: categoryId,
          airPlaySecurityProfilePresetId: preset.uniqueId,
          displayName: '',
        };

        return safeMap(
          this.locationAirplaySecurityConfigurationService.addAirPlaySecurityProfileToLocation(categoryId, {
            airPlaySecurityProfilePresetId: preset.uniqueId,
          }),
          (configuration) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAdded({
              configuration: airplaySecurityProfileFrom(configuration),
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAddedError({
              configuration: locationConfiguration,
              categoryType,
              error,
            })
        );
      }
      case 'Room': {
        const roomConfiguration: CategoryAirplaySecurityProfileConfiguration = {
          type: 'Room',
          id: categoryId,
          airPlaySecurityProfilePresetId: preset.uniqueId,
          displayName: '',
        };

        return safeMap(
          this.roomAirplaySecurityConfigurationService.addAirPlaySecurityProfileToRoom(categoryId, {
            airPlaySecurityProfilePresetId: preset.uniqueId,
          }),
          (configuration) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAdded({
              configuration: airplaySecurityProfileFrom(configuration),
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileAddedError({
              configuration: roomConfiguration,
              categoryType,
              error,
            })
        );
      }
      default:
        return EMPTY;
    }
  };

  private readonly delete$ = (
    categoryType: CategoryType,
    categoryId: string,
    configuration: CategoryAirplaySecurityProfileConfiguration
  ): Observable<Action> => {
    switch (categoryType) {
      case 'Brand':
        return safeMap(
          this.brandAirplaySecurityConfigurationService.deleteAirPlaySecurityProfileFromBrand(categoryId),
          () =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeleted({
              configuration,
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeletedError({
              configuration,
              categoryType,
              error,
            })
        );
      case 'Location':
        return safeMap(
          this.locationAirplaySecurityConfigurationService.deleteAirPlaySecurityProfileFromLocation(categoryId),
          () =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeleted({
              configuration,
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeletedError({
              configuration,
              categoryType,
              error,
            })
        );
      case 'Room':
        return safeMap(
          this.roomAirplaySecurityConfigurationService.deleteAirPlaySecurityProfileFromRoom(categoryId),
          () =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeleted({
              configuration,
              categoryType,
            }),
          (error) =>
            CategoryAirplaySecurityProfileApiActions.categoryProfileDeletedError({
              configuration,
              categoryType,
              error,
            })
        );
      default:
        return EMPTY;
    }
  };
}
